import {isEmpty} from 'lodash';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {
  useBuildCancelToken,
  useDebounce,
} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {CancelError} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownLoader} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownLoader';
import {
  DropdownVirtualList,
  ScrollHandler,
} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownVirtualList';
import {DropdownItemCheckbox} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {AsyncSearchDropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {SearchDropdownHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdownHeader';
import {computeIsNearBottomOfScrollableList} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdownHelpers';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {SearchTagModel} from '../../../../../models/api/searchTagModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {TagDropdownItem} from '../../../../common/dropdowns/tagDropdownItem';
import {getFetchTagsAsync} from '../../searchBar/queryBuilder/inboxesSearchQueryBuilderHelpers';
import {SearchSelectedTagDropdownItem} from './selectedTagDropdownItem';

interface SearchHintTagDropdownProps {
  popoverProps: PopoverTransferProps;
  selectedTags: ReadonlyArray<TagModel>;
  onSelectedTagsChange: (tags: ReadonlyArray<TagModel>) => void;
}

const DROPDOWN_WIDTH = 320;

const StyledHeaderDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const SearchHintTagDropdown: FC<SearchHintTagDropdownProps> = (props) => {
  const {popoverProps, selectedTags, onSelectedTagsChange} = props;
  const [checkedTags, setCheckedTags] = useState<TagModel[]>([...selectedTags] ?? []);
  const tagsFetcher = useAppSelector(getFetchTagsAsync);
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const {
    status,
    response,
    performSearchDebounced,
    performSearchWithoutDebounce,
    fetchMoreData,
    setLoadingDebounced,
  } = useAsyncSearchDropdownQueries(trimmedQuery, {
    onRequestData: tagsFetcher,
  });

  useEffect(() => {
    setLoadingDebounced();
    // Never debounce the empty query
    if (isEmpty(trimmedQuery)) {
      performSearchWithoutDebounce([], undefined);
    } else {
      performSearchDebounced([], undefined);
    }
  }, [trimmedQuery]);

  const onBlur = useCallback(() => {
    if (
      checkedTags.length !== selectedTags.length ||
      !checkedTags.every((tag) => selectedTags.some((selectedTag) => selectedTag.alias === tag.alias))
    ) {
      onSelectedTagsChange(checkedTags);
    }
  }, [checkedTags, selectedTags, onSelectedTagsChange]);

  const onTagClick = useCallback(
    (clickedTag: TagModel) => {
      setCheckedTags((prevTags) => {
        const tagIndex = prevTags.findIndex((tag) => tag.alias === clickedTag.alias);
        if (tagIndex >= 0) {
          return prevTags.filter((tag) => tag.alias !== clickedTag.alias);
        }

        return [...prevTags, clickedTag];
      });
    },
    [setCheckedTags],
  );

  const onScroll: ScrollHandler = useCallback(
    (scrollContext) => {
      // Only load more if we're near the bottom of the list.
      const isNearBottom = computeIsNearBottomOfScrollableList(scrollContext);
      if (!isNearBottom) {
        return;
      }

      fetchMoreData();
    },
    [fetchMoreData],
  );

  const isSearching = trimmedQuery.length > 0;

  return (
    <Dropdown
      {...popoverProps}
      width={DROPDOWN_WIDTH}
      onRequestClose={() => {
        onBlur();
        popoverProps.onRequestClose();
      }}
    >
      <SearchDropdownHeader
        title={
          <StyledHeaderDiv>
            <Trans id="search-hint-tag">Tag</Trans>
            <TooltipCoordinator
              maxWidth={240}
              render={() => (
                <Trans id="search-hint-tag-tooltip">
                  Only conversations matching all selected tags and other filters will appear in search
                  results.
                </Trans>
              )}
            />
          </StyledHeaderDiv>
        }
        onSearchInputEnter={() => {}}
        shouldIncludeSearchIcon
        value={query}
        onChange={setQuery}
        rightIcon={
          checkedTags.length > 0 ? (
            <Button
              variant={ButtonVariantsEnum.LINK}
              size={VisualSizesEnum.SMALL}
              onClick={() => {
                onSelectedTagsChange([]);
                popoverProps.onRequestClose();
              }}
            >
              <Trans id="clear-refinement-button-link">Clear</Trans>
            </Button>
          ) : null
        }
      />
      <DropdownCategory>
        <Trans id="search-hint-tag-dropdown-all">All tags</Trans>
      </DropdownCategory>

      {status === AsyncStatusesEnum.LOADED && !response.data.length && isSearching ? (
        <DropdownEmpty />
      ) : (
        <TagList
          popoverProps={popoverProps}
          tags={response.data}
          selectedTags={checkedTags}
          initialTags={selectedTags}
          showSelectedTagsFirst={!isSearching}
          handleSelect={onTagClick}
          onScroll={onScroll}
        />
      )}
      {status === AsyncStatusesEnum.LOADING && (
        <DropdownLoader status={status} onRetryClick={() => performSearchWithoutDebounce([], undefined)} />
      )}
    </Dropdown>
  );
};

const TagList = ({
  tags,
  selectedTags,
  initialTags,
  showSelectedTagsFirst,
  popoverProps,
  handleSelect,
  onScroll,
}: {
  tags: readonly SearchTagModel[];
  selectedTags: readonly TagModel[];
  initialTags: readonly TagModel[];
  showSelectedTagsFirst: boolean;
  popoverProps: PopoverTransferProps;
  handleSelect: (tag: TagModel) => void;
  onScroll?: ScrollHandler;
}) => {
  if (showSelectedTagsFirst) {
    const unselectedSearchResults = tags.filter(
      (tag) => !initialTags.some((initalTag) => tag[0].alias === initalTag.alias),
    );

    return (
      <DropdownVirtualList
        {...popoverProps}
        width={DROPDOWN_WIDTH}
        maxHeight={300}
        rowCount={initialTags.length + unselectedSearchResults.length}
        onRequestClose={() => {}}
        onScroll={onScroll}
        renderRow={(index) => {
          if (index < initialTags.length) {
            const tag = initialTags[index];
            const isTagSelected = selectedTags.some((selectedTag) => tag.alias === selectedTag.alias);

            return (
              <SearchSelectedTagDropdownItem
                selectedTag={tag}
                onSelect={() => handleSelect(tag)}
                isChecked={isTagSelected}
              />
            );
          }

          const tag = unselectedSearchResults[index - initialTags.length];

          if (!tag) {
            return null;
          }
          const isTagSelected = selectedTags.some((selectedTag) => tag[0].alias === selectedTag.alias);

          return (
            <TagDropdownItem
              tag={tag[0]}
              nestingLevel={0}
              tagsPath={tag}
              isChecked={isTagSelected}
              onSelect={(e) => {
                e.preventDefault();
                handleSelect(tag[0]);
              }}
              renderRightContent={() => <DropdownItemCheckbox isChecked={isTagSelected} />}
            />
          );
        }}
      />
    );
  }

  return (
    <DropdownVirtualList
      {...popoverProps}
      width={DROPDOWN_WIDTH}
      maxHeight={300}
      rowCount={tags.length}
      onScroll={onScroll}
      onRequestClose={() => {}}
      renderRow={(index) => {
        const tag = tags[index];

        if (!tag) {
          return null;
        }

        const isTagSelected = selectedTags.some((selectedTag) => tag[0].alias === selectedTag.alias);

        return (
          <TagDropdownItem
            tag={tag[0]}
            nestingLevel={0}
            tagsPath={tag}
            isChecked={isTagSelected}
            onSelect={() => handleSelect(tag[0])}
            renderRightContent={() => <DropdownItemCheckbox isChecked={isTagSelected} />}
          />
        );
      }}
    />
  );
};

const defaultGracePeriod = 500;
const defaultAutocompleteDebounceInMs = 200;

// TODO(ldemarne): Refactor with asyncSearchDropdown's.
interface AsyncSearchDropdownState<T, U> {
  query: string;
  data: ReadonlyArray<T>;
  canRequestMore: boolean;
  paginationData: U | undefined;
}

function useAsyncSearchDropdownQueries<T, U>(
  trimmedQuery: string,
  props: Pick<
    AsyncSearchDropdownProps<T, U>,
    'gracePeriodInMs' | 'onRequestData' | 'findRowHeight' | 'searchDebounceInMs'
  >,
) {
  const {gracePeriodInMs, onRequestData, searchDebounceInMs} = props;

  const [status, setStatus] = useState(AsyncStatusesEnum.LOADING);
  const [response, setResponse] = useState<AsyncSearchDropdownState<T, U>>({
    query: '',
    data: [],
    canRequestMore: false,
    paginationData: undefined,
  });

  const isRequestingMoreRef = useRef(false);
  const {buildCancelToken} = useBuildCancelToken();

  const setLoadingDebounced = useDebounce(
    () => setStatus(AsyncStatusesEnum.LOADING),
    gracePeriodInMs || defaultGracePeriod,
  );

  const performSearch = async (existingData: ReadonlyArray<T>, paginationData: U | undefined) => {
    const requestCancelToken = buildCancelToken();

    try {
      const newResponse = await onRequestData(trimmedQuery, existingData, requestCancelToken, paginationData);
      setLoadingDebounced.cancel();

      setStatus(AsyncStatusesEnum.LOADED);

      // FIXME
      //   if (isFunction(findRowHeight)) {
      //     ref?.current?.resetHeightCache();
      //   }

      setResponse({
        query: trimmedQuery,
        data: newResponse.data,
        canRequestMore: newResponse.canRequestMore,
        paginationData: newResponse.paginationData,
      });
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }
      setLoadingDebounced.cancel();
      setStatus(AsyncStatusesEnum.FAILED);
    }
  };

  const performSearchDebounced = useDebounce(
    performSearch,
    searchDebounceInMs ?? defaultAutocompleteDebounceInMs,
  );

  const performSearchWithoutDebounce = async (
    existingData: ReadonlyArray<T>,
    paginationData: U | undefined,
  ) => {
    performSearchDebounced.cancel();
    await performSearch(existingData, paginationData);
  };

  const fetchMoreData = async () => {
    if (!response.canRequestMore || isRequestingMoreRef.current) {
      return;
    }
    isRequestingMoreRef.current = true;
    await performSearchWithoutDebounce(response.data, response.paginationData);
    isRequestingMoreRef.current = false;
  };

  return {
    status,
    response,
    performSearchDebounced,
    performSearchWithoutDebounce,
    fetchMoreData,
    setLoadingDebounced,
  };
}
